import axios from 'axios';
import { storageKeys } from '../constants';
import {
  TChannelDetail,
} from 'types';

function getAuthFromStorage(): {
  accessToken: string | null;
  userLineId: string | null
} {
  return {
    accessToken: localStorage.getItem(storageKeys.ACCESS_TOKEN),
    userLineId: localStorage.getItem(storageKeys.USER_ID),
  };
}

const getChannelDetail = async (id: string): Promise<TChannelDetail> => {
  // Check if we have accessToken (UserMale authentication) or Firebase token
  const { accessToken, userLineId } = getAuthFromStorage();

  // If we have accessToken (UserMale auth), use the male endpoint
  // The male endpoint uses accessToken from Authorization header (set by axios interceptor)
  if (accessToken) {
    const res = await axios
      .get(`messages/channel/${id}/male/${userLineId}`)
      .then(function (response) {
        return response.data;
      });
    return res;
  }

  // Otherwise, use the regular endpoint (Firebase auth)
  const res = await axios
    .get(`messages/channel/${id}`)
    .then(function (response) {
      return response.data;
    });

  return res;
};

const updateSeenTime = async (id: string): Promise<boolean> => {
  // Check if we have accessToken (UserMale authentication) or Firebase token
  const { accessToken, userLineId } = getAuthFromStorage();

  // If we have accessToken, use the male endpoint
  if (accessToken) {
    const res = await axios
      .patch(`messages/channel/${id}/male/${userLineId}/seen`)
      .then(function (response) {
        return response.data;
      });
    return res.success || true;
  }

  // Otherwise, use the regular endpoint (Firebase auth)
  const res = await axios
    .patch(`messages/channel/${id}/seen`)
    .then(function (response) {
      return response.data;
    });

  return res;
};

const updateChannelActive = async (
  id: string,
  text?: string,
): Promise<boolean> => {
  // Check if we have accessToken (UserMale authentication) or Firebase token
  const { accessToken, userLineId } = getAuthFromStorage();

  // If we have accessToken, use the male endpoint
  // text は運営宛 Web Push 通知のプレビュー本文（冒頭のみ表示。file のみなら空）
  if (accessToken) {
    const res = await Promise.all([
      axios
        .patch(`messages/channel/${id}/male/${userLineId}/sent`, { text })
        .then(function (response) {
          return response.data;
        }),
    ]).then(() => true);
    return res;
  }

  // Otherwise, use the regular endpoint (Firebase auth)
  const res = await Promise.all([
    axios.patch(`messages/channel/${id}/sent`).then(function (response) {
      return response.data;
    }),
  ]).then(() => true);

  return res;
};

// axios(XHR) は閉じるページで打ち切られるため、離脱時の /sent だけ keepalive の fetch で送る
const updateChannelActiveOnPageHide = (id: string, text: string): void => {
  const { accessToken, userLineId } = getAuthFromStorage();
  if (!accessToken || !userLineId) return;

  fetch(axios.getUri({ url: `messages/channel/${id}/male/${userLineId}/sent` }), {
    method: 'PATCH',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ text }),
  }).catch(() => undefined);
};

const getNewLineChannelToAddFriend = async (
  channelId: string
): Promise<string | null> => {
  const { userLineId } = getAuthFromStorage();

  const res = await axios
    .get<string | null>(`messages/channel/${channelId}/male/${userLineId}/line-channel-add-friend`)
    .then((response) => response.data)
    .catch((error) => {
      console.warn('[getNewLineChannelToAddFriend] API error:', error);
      return null;
    });

  return res ?? null;
};

const deleteChannelMessage = async (
  channelId: string,
  timetoken: string,
): Promise<{ success: boolean }> => {
  const { userLineId } = getAuthFromStorage();

  const res = await axios
    .delete<{ success: boolean }>(
      `messages/channel/${encodeURIComponent(channelId)}/male/${userLineId}/messages/${encodeURIComponent(timetoken)}`,
    )
    .then((response) => response.data);

  return res;
};

/**
 * 通知APIの再試行可否を判定する。
 * 応答が無い（ネットワーク断・タイムアウト）か5xxのときだけ再送する。
 * @param error axiosが返したエラー
 * @returns 再試行してよいならtrue
 */
const isRetriableRecordError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number } } | null)
    ?.response;
  if (!response) return true;

  return (response.status ?? 0) >= 500;
};

/**
 * 送信したメッセージを運営の未読集計用の台帳へ記録する。
 * 運営側のバッジはこの台帳だけを数えるため、送信のたびに必ず呼ぶ
 * （/sent はデバウンスされるので連投分を取りこぼす）。
 * backend 側は messageTimetoken で冪等化されているため再送しても二重に数えない。
 * @param id チャンネルID
 * @param messageTimetoken publish済みメッセージのPubNub timetoken
 * @returns 記録できたらtrue。記録できないまま通常の通知を送ると、運営側は
 *   一覧のバッジだけをローカルに増やしてサイドバーの合計と食い違うため、
 *   呼び出し元はこの結果を通知に載せること
 */
const recordSentMessage = async (
  id: string,
  messageTimetoken: string,
): Promise<boolean> => {
  const { accessToken, userLineId } = getAuthFromStorage();
  // Firebase認証経路には台帳APIが無い（運営バッジの対象はucode経由のチャットのみ）
  if (!accessToken || !userLineId) return false;

  const retryDelaysMs = [0, 250, 1000] as const;
  // 送信側はこの記録を await してから参加者へ通知する（順序保証）。
  // axios のデフォルトは無タイムアウトのため、接続がハングすると送信UIが
  // 塞がったままになる。1回あたりの待ちを固定して最悪時間を有限にする
  const attemptTimeoutMs = 3000;
  let lastError: unknown;

  for (const delayMs of retryDelaysMs) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      await axios.post(
        `messages/channel/${id}/male/${userLineId}/messages`,
        { messageTimetoken },
        { timeout: attemptTimeoutMs },
      );
      return true;
    } catch (error) {
      lastError = error;
      // 4xxは再試行しても結果が変わらないので即座に諦める
      if (!isRetriableRecordError(error)) break;
    }
  }

  console.error(
    '❌ [SEND_MESSAGE] Failed to record message for unread count:',
    lastError,
  );
  return false;
};

export {
  getChannelDetail,
  getNewLineChannelToAddFriend,
  updateChannelActive,
  updateChannelActiveOnPageHide,
  updateSeenTime,
  deleteChannelMessage,
  recordSentMessage,
};
